import React, {useContext, useMemo, useState} from 'react'

import {useFilter} from '../hooks'

interface MaterialDemandFiltersState {
  selectedMaterialIds: number[]
  toggleMaterialId: (value: number) => void
  selectAllMaterials: () => void
  selectedDate: string | undefined // date in format 'YYYY-MM-DD'
  toggleSelectedDate: (date: string) => void
  clearSelectedDate: () => void
  changeIsEditing: (isEditing: boolean) => void
  isEditing: boolean
}

const MaterialDemandFiltersContext = React.createContext<MaterialDemandFiltersState | undefined>(
  undefined
)

export const useMaterialDemandFilters = () => {
  const context = useContext(MaterialDemandFiltersContext)
  if (!context)
    throw new Error(
      'Cannot use MaterialDemandFilterContext outside of a MaterialDemandFilterProvider provider'
    )
  return context
}

export const MaterialDemandFiltersProvider: React.FC<{children: React.ReactNode}> = ({
  children
}) => {
  const {
    values: selectedMaterialIds,
    toggleValue: toggleMaterialId,
    resetValues: selectAllMaterials
  } = useFilter<number>()
  const {
    values: [selectedDate],
    toggleValue: toggleSelectedDate,
    clearValues: clearSelectedDate
  } = useFilter<string>({mode: 'single'})

  const [isEditing, changeIsEditing] = useState(false)

  const memoizedState = useMemo(
    () => ({
      selectedMaterialIds,
      toggleMaterialId,
      selectAllMaterials,
      selectedDate,
      toggleSelectedDate,
      clearSelectedDate,
      isEditing,
      changeIsEditing
    }),
    [
      selectedMaterialIds,
      selectedDate,
      toggleMaterialId,
      selectAllMaterials,
      toggleSelectedDate,
      clearSelectedDate,
      isEditing,
      changeIsEditing
    ]
  )

  return (
    <MaterialDemandFiltersContext.Provider value={memoizedState}>
      {children}
    </MaterialDemandFiltersContext.Provider>
  )
}
